import { CrudOperation } from '@/lib/apiShared';
import { axiosClientAdmin } from '@/lib/auth';
import {
  ConnectedDeviceFilter,
  ConnectedDeviceFilterChoice,
  ExternalConnectionDto,
  ExternalConnectionLimitedDto,
  ExternalConnectionType,
} from '@/lib/integrationTypes/externalConnectionTypes';
import {
  IntegrationScriptLogDto,
  NVRDto,
  SubCameraDto,
} from '@/lib/integrationTypes/integrationTypes';
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const BaseCacheKey = 'external-connections';


interface ExternalConnectionResponse {
  externalConnection: ExternalConnectionDto;
  devices: { device: NVRDto; cameras: SubCameraDto[] }[];
}

interface ConnectionDataTypeResponse extends Omit<ExternalConnectionLimitedDto, 'type'> {
  type: string;
}
interface ListConnectionResponse {
  externalConnections: ConnectionDataTypeResponse[];
}

export const useListConnections = () =>
  useQuery({
    queryKey: [BaseCacheKey],
    queryFn: async () => {
      return await axiosClientAdmin
        .get<ListConnectionResponse>('/Integration/ExternalConnection')
        .then((response) =>
          response.data.externalConnections.map((x) => ({
            ...x,
            type: ExternalConnectionType[x.type as keyof typeof ExternalConnectionType],
          } as ExternalConnectionLimitedDto))
        );
    },
  });

export const useCreateExternalConnection = ({ queryClient }: { queryClient: QueryClient }) =>
  useMutation({
    mutationFn: async (data: Partial<ExternalConnectionDto>) => {
      return await axiosClientAdmin
        .put<ExternalConnectionResponse>('/Integration/ExternalConnection', data)
        .then((response) => response.data);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [BaseCacheKey],
      }),
  });
export const useReadExternalConnection = ({ id }: { id: string }) =>
  useQuery({
    queryKey: [BaseCacheKey, id],
    queryFn: async () => {
      return await axiosClientAdmin
        .get<ExternalConnectionResponse>(`/Integration/ExternalConnection/${id}`)
        .then((response) => response.data);
    },
    retry: (_count, error) => error instanceof AxiosError && error.status !== 404,
  });

export const useUpdateExternalConnection = ({
  queryClient,
  id,
}: {
  queryClient: QueryClient;
  id: string;
}) =>
  useMutation({
    mutationFn: async (data: ExternalConnectionDto) => {
      return await axiosClientAdmin
        .post<ExternalConnectionResponse>(`/Integration/ExternalConnection/${id}`, data)
        .then((response) => response.data);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [BaseCacheKey, id],
      }),
  });
export const useDeleteExternalConnection = ({
  queryClient,
  id,
}: {
  queryClient: QueryClient;
  id: string;
}) =>
  useMutation({
    mutationFn: async () => {
      return await axiosClientAdmin
        .delete<ExternalConnectionResponse>(`/Integration/ExternalConnection/${id}`)
        .then((response) => response.data);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [BaseCacheKey],
      }),
  });

export interface TestExternalConnection {
  integrationId: string;
  integrationFields: { [key: string]: string | boolean | number };
}
export interface ExternalConnectionIndexed {
  deviceCreationKeyValues: ConnectedDeviceFilter[];
  deviceCreationChoices: ConnectedDeviceFilterChoice[];
  scriptLog: IntegrationScriptLogDto[];
}

export const useTestExternalConnection = () => {
  return useMutation({
    mutationFn: async (data: TestExternalConnection) => {
      return await axiosClientAdmin
        .post<ExternalConnectionIndexed>('/Integration/ExternalConnection/test-connection', data)
        .then((response) => response.data);
    },
  });
};

export const useReindexExternalConnection = ({
  queryClient,
  id,
}: {
  queryClient: QueryClient;
  id: string;
}) =>
  useMutation({
    mutationFn: async (data: ExternalConnectionDto | null) => {
      return await axiosClientAdmin
        .post<{ result: ExternalConnectionIndexed }>(`/Integration/ExternalConnection/${id}/reindex`, {
          externalConnection: data,
          operation: CrudOperation.Update,
        })
        .then((response) => response.data.result);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [BaseCacheKey, id],
      }),
  });


export interface ExternalServiceOptionsResponse {
  externalOptions: ExternalServiceOption[];
}
export interface ExternalServiceOption {
  id: string;
  name: string;
  description: string;
  integrationId: string;
  lastScanTime: string;
  created: string;
  updated: string;
  deviceCreationKeyValues: ConnectedDeviceFilter[];
  deviceCreationChoices: ConnectedDeviceFilterChoice[];
}

export const useFetchExternalServiceOptions = () => {
  return useQuery({
    queryKey: ['external-service-options'],
    queryFn: async () => {
      return await axiosClientAdmin.get<ExternalServiceOptionsResponse>(
        '/Integration/ExternalConnection/services/options'
      ).then((response) => response.data);
    },
  });
}